import { ContentCard } from '../../styles';
import { Spacer } from '@kizen/kds/Spacer';
import { Button } from '@kizen/kds/Button';
import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import Loader from '__components/Kizen/Loader';
import { Dots } from './styles';
import { ResolvedPlanDetails } from '../hooks/useSelectedPlanResolution';
import { useMemo } from 'react';

type PlanSummaryProps = {
  currentSelectedCorePlatformPlan: ResolvedPlanDetails;
  currentSelectedAutomationPlan: ResolvedPlanDetails;
  currentSelectedMarketingPlan: ResolvedPlanDetails;
  numUsersForCalculation: number;
  currentMonthlyTotal: number;
  isDirty: boolean;
  billingPreviewResponse?: any;
  clickSaveChanges: () => void;
  showResetSelectionsModal: () => void;
  pendingRecalculation: boolean;
  hasPendingDowngrade: boolean;
};

export const PlanSummary = (props: PlanSummaryProps) => {
  const {
    currentSelectedCorePlatformPlan,
    numUsersForCalculation,
    currentSelectedAutomationPlan,
    currentSelectedMarketingPlan,
    currentMonthlyTotal,
    isDirty,
    billingPreviewResponse,
    clickSaveChanges,
    showResetSelectionsModal,
    pendingRecalculation,
    hasPendingDowngrade,
  } = props;

  const hasProratedAmountToDisplay = useMemo(() => {
    const summary = billingPreviewResponse?.upgrade_cost_summary ?? {};
    return Object.keys(summary).length > 0;
  }, [billingPreviewResponse]);

  const { t } = useTranslation();

  return (
    <ContentCard loadable>
      <div>
        <Typography weight="semibold" size="lg">
          {t('Plan Summary ({{period}})', { period: t('Monthly') })}
        </Typography>
        <Spacer size={30} />
        <div>
          <div>
            <Typography size="lg">
              {t('Kizen Core Platform - {{planName}}', {
                planName: currentSelectedCorePlatformPlan?.name,
              })}
            </Typography>
            <Spacer size={15} />
            <div className="pl-4 flex items-end">
              <Typography className="pr-1">
                {t('{{userCount}} Users', {
                  userCount: numUsersForCalculation?.toLocaleString(),
                })}
              </Typography>
              <Dots className="grow" />
              <Typography className="pl-1">
                {currentSelectedCorePlatformPlan?.price?.symbol}
                {(
                  numUsersForCalculation *
                  (currentSelectedCorePlatformPlan?.price?.amount ?? 0)
                ).toLocaleString()}
              </Typography>
            </div>
          </div>
        </div>
        <Spacer size={30} />
        <div>
          <div>
            <Typography size="lg">
              {t('Automations - {{planName}}', {
                planName: currentSelectedAutomationPlan?.name,
              })}
            </Typography>
            <Spacer size={15} />
            <div className="pl-4 flex items-end">
              <Typography className="pr-1">
                {t('{{limit}} Monthly Automated Tasks', {
                  limit:
                    currentSelectedAutomationPlan?.limits?.monthly_tasks_limit?.toLocaleString(),
                })}
              </Typography>
              <Dots className="grow" />
              <Typography className="pl-1">
                {currentSelectedAutomationPlan?.price?.symbol}
                {currentSelectedAutomationPlan?.price?.amount ?? 0}
              </Typography>
            </div>
          </div>
        </div>
        <Spacer size={30} />
        <div>
          <div>
            <Typography size="lg">
              {t('Marketing Platform - {{planName}}', {
                planName: currentSelectedMarketingPlan?.name,
              })}
            </Typography>
            <Spacer size={15} />
            <div className="pl-4 flex items-end">
              <Typography className="pr-1">
                {t('{{limit}} Monthly Emails', {
                  limit:
                    currentSelectedMarketingPlan?.limits?.monthly_emails_limit?.toLocaleString(),
                })}
              </Typography>
              <Dots className="grow" />
              <Typography className="pl-1">
                {currentSelectedMarketingPlan?.price?.symbol}
                {currentSelectedMarketingPlan?.price?.amount ?? 0}
              </Typography>
            </div>
          </div>
        </div>
        <Spacer size={30} />
        <hr className="my-0 mx-12" />
        <Spacer size={30} />
        <div className="flex items-center">
          <Typography size="md">{t('Current Monthly Total')}</Typography>
          <Typography className="ml-auto">${currentMonthlyTotal}</Typography>
        </div>
        {hasPendingDowngrade ? (
          <div className="max-w-[75%]">
            <Spacer size={10} />
            <Typography size="sm" fontStyle="italic" color={'gray-600'}>
              {t('Pending plan downgrades may impact your monthly total')}
            </Typography>
            <Spacer size={10} />
          </div>
        ) : (
          <Spacer size={20} />
        )}
        {isDirty ? (
          <div className="flex items-center">
            <Typography size="lg">{t('New Monthly Total')}</Typography>
            <Typography className="ml-auto" size="lg">
              ${billingPreviewResponse?.monthly_cost_summary?.total}
            </Typography>
          </div>
        ) : null}

        {hasProratedAmountToDisplay ? (
          <>
            <Spacer size={10} />
            <div className="flex items-center">
              <Typography size="md">
                {t('Prorated Amount Billed Today')}
              </Typography>
              <Typography className="ml-auto" size="md">
                ${billingPreviewResponse?.upgrade_cost_summary?.total}
              </Typography>
            </div>
          </>
        ) : null}
        <Spacer size={20} />
        <div className="flex justify-center gap-15">
          <Button disabled={!isDirty} onClick={clickSaveChanges}>
            {t('Save New Plan')}
          </Button>
          <Button
            variant="text"
            color="warning"
            onClick={() => {
              if (isDirty) {
                showResetSelectionsModal();
              }
            }}
            disabled={!isDirty}
          >
            {t('Cancel Changes')}
          </Button>
        </div>
      </div>
      {pendingRecalculation ? (
        <div className="absolute top-0 right-0 left-0 bottom-0 flex items-center justify-center bg-[rgba(255,255,255,0.5)] flex items-center">
          <Loader loading paddingTop="0" />
        </div>
      ) : null}
    </ContentCard>
  );
};
